<template>
  <div v-if="options.length > 0" class="fg-tariff">
    <p v-if="isReadOnly" class="fg-tariff-readonly">
      <strong>{{ options[0]!.label }}</strong>
      <span class="fg-tariff-readonly__price">{{ formatPrice(options[0]!.amount, currency, locale) }}</span>
    </p>

    <template v-else>
      <div class="fg-tariff__options">
        <button
          v-for="opt in options"
          :key="opt.type"
          type="button"
          class="fg-tariff__btn"
          :class="{ 'is-active': selectedType === opt.type }"
          @click="pick(opt.type)"
        >
          <span class="fg-tariff__btn-check"><Icon name="check" :size="11" /></span>
          <span class="fg-tariff__btn-label">{{ opt.label }}</span>
          <span class="fg-tariff__btn-price">{{ formatPrice(opt.amount, currency, locale) }}</span>
          <span v-if="opt.balance > 0" class="fg-tariff__btn-balance">
            {{ t('tariff.balance') }} {{ formatPrice(opt.balance, currency, locale) }}
          </span>
        </button>
      </div>

      <div v-if="detailKind" class="fg-tariff__detail">
        <div class="fg-tariff__detail-head">
          <Icon name="info" :size="13" class="fg-tariff__detail-head-icon" />
          <span>{{ detailKind === 'presale' ? t('tariff.presaleTitle') : t('tariff.optionTitle') }}</span>
        </div>
        <dl class="fg-tariff__detail-list">
          <div class="fg-tariff__row fg-tariff__row--main">
            <dt>{{ t('tariff.payToday') }}</dt>
            <dd>{{ formatPrice(currentAmount, currency, locale) }}</dd>
          </div>
          <div v-if="currentBalance > 0" class="fg-tariff__row">
            <dt>{{ t('tariff.remaining') }}</dt>
            <dd>{{ formatPrice(currentBalance, currency, locale) }}</dd>
          </div>
          <div v-if="expirationLabel" class="fg-tariff__row">
            <dt>{{ detailKind === 'presale' ? t('tariff.dueDate') : t('tariff.optionValidUntil') }}</dt>
            <dd>{{ expirationLabel }}</dd>
          </div>
        </dl>
        <p class="fg-tariff__detail-desc">
          {{ detailKind === 'presale' ? t('tariff.presaleDesc') : t('tariff.optionDesc') }}
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from '../../store'
import { useI18n } from '../../i18n'
import { pickCheapestByType, tariffDisplayAmount } from '../../../../shared/rules'
import type { Availability, Tariff } from '../../../../shared/types'
import { formatPrice } from '../../utils/format'
import Icon from '../Icon.vue'

type Direction = 'outward' | 'return'

const props = defineProps<{
  availabilities: Array<{ direction: Direction; availability: Availability | null }>
}>()

const store = useStore()
const { t, locale } = useI18n()

const TYPES = ['ticket', 'presale', 'option'] as const

const activeAvs = computed(() => props.availabilities.filter((x) => x.availability !== null))

// Pour chaque type, agrège (sum amount/balance, max expiration) sur toutes les
// directions qui le proposent.
const options = computed(() => {
  const rows: Array<{ type: string; label: string; amount: number; balance: number; expiration: number; tariffs: Array<{ direction: Direction; tariff: Tariff }> }> = []
  for (const type of TYPES) {
    const tariffs: Array<{ direction: Direction; tariff: Tariff }> = []
    let amount = 0
    let balance = 0
    let expiration = 0
    for (const { direction, availability } of activeAvs.value) {
      if (!availability) continue
      const tariff = pickCheapestByType(availability, type)
      if (!tariff) continue
      tariffs.push({ direction, tariff })
      amount += tariffDisplayAmount(tariff)
      balance += tariff.balance_amount ?? 0
      if (tariff.expiration_date && tariff.expiration_date > expiration) expiration = tariff.expiration_date
    }
    if (tariffs.length === 0) continue
    rows.push({ type, label: t(`tariff.${type}`), amount, balance, expiration, tariffs })
  }
  return rows
})

const currency = computed(() => {
  for (const { availability } of activeAvs.value) {
    const first = Object.values(availability?.tariffs ?? {})[0]
    if (first?.pricing?.display_currency) return first.pricing.display_currency
    if (first?.currency) return first.currency
  }
  return 'EUR'
})

const isReadOnly = computed(() => options.value.length === 1)
const selectedType = computed(() => store.selectedTariffType.outward || 'ticket')

const currentRow = computed(() => options.value.find((o) => o.type === selectedType.value) ?? options.value[0] ?? null)
const currentAmount = computed(() => currentRow.value?.amount ?? 0)
const currentBalance = computed(() => currentRow.value?.balance ?? 0)

const detailKind = computed<'presale' | 'option' | null>(() => {
  if (selectedType.value === 'presale') return 'presale'
  if (selectedType.value === 'option') return 'option'
  return null
})

const expirationLabel = computed(() => {
  const exp = currentRow.value?.expiration ?? 0
  if (!exp) return ''
  const date = new Date(exp * 1000)
  if (Number.isNaN(date.getTime())) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`
})

function pick(type: string): void {
  const row = options.value.find((o) => o.type === type)
  if (!row) return
  // On ne choisit QUE le type par direction ; la dérivation des tarifs (groupe 1
  // ET groupe 2) est centralisée côté Results (watch sur selectedTariffType).
  // Direction sans ce type → fallback ticket (cas asymétrique aller=presale /
  // retour=ticket only).
  const directions = new Set(activeAvs.value.map((x) => x.direction))
  for (const direction of directions) {
    const hasType = row.tariffs.some((tt) => tt.direction === direction)
    store.selectedTariffType[direction] = hasType ? type : 'ticket'
  }
}
</script>
